import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  VerifiedUser,
  CardMembership,
  AdminPanelSettings,
  Settings as SettingsIcon,
  Assessment,
  Logout,
  Payment as PlansIcon,
  Analytics as AnalyticsIcon,
  FactCheck as AboutMeVerificationIcon,
  PhotoCamera as PhotoVerificationIcon
  , ContactMail as PaymentAttemptsIcon
} from '@mui/icons-material';
import adminService from '../../services/adminService';
import symLogo from '../../assets/sym-logo.jpg';
import { logout } from '../../redux/slices/adminSlice';
import { persistor } from '../../redux/store';
import { ADMIN_ROLES } from '../../constants/adminRoles';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Users', icon: <People />, path: '/users' },
  { text: 'Verifications', icon: <VerifiedUser />, path: '/verifications' },
  { text: 'About Me Verification', icon: <AboutMeVerificationIcon />, path: '/about-me-verification' },
  { text: 'Photo Verification', icon: <PhotoVerificationIcon />, path: '/photo-verification' },
  { text: 'Documents', icon: <VerifiedUser />, path: '/documents' },
  { text: 'Plans', icon: <PlansIcon />, path: '/plans' },
  { text: 'Plans Dashboard', icon: <AnalyticsIcon />, path: '/plans/dashboard' },
  { text: 'Memberships', icon: <CardMembership />, path: '/memberships' },
  { text: 'Payment Follow-ups', icon: <PaymentAttemptsIcon />, path: '/payment-attempts' },
  { text: 'Admin Roles', icon: <AdminPanelSettings />, path: '/admin-roles' },
  // { text: 'Activity Logs', icon: <Assessment />, path: '/logs' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const dispatch = useDispatch();
  const currentAdmin = useSelector((state) => state.admin.admin);

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.path === '/admin-roles') {
      return currentAdmin?.role === ADMIN_ROLES.SUPERADMIN;
    }

    if (item.path === '/settings') {
      return currentAdmin?.role === ADMIN_ROLES.SUPERADMIN;
    }

    return true;
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await adminService.logout();
      dispatch(logout());
      persistor.purge(); // Clear persisted state
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ minHeight: 72, px: 2 }}>
        <img
          src={symLogo}
          alt="SYM Matrimonial Services"
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: 12,
            border: '2px solid rgba(0,0,0,0.08)',
          }}
        />
        <Box>
          <Typography variant="subtitle1" noWrap component="div" sx={{ fontWeight: 700 }}>
            SYM Matrimonial
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin Console
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1.5, py: 1.5, flexGrow: 1 }}>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
            >
              <ListItemIcon sx={{ minWidth: 38, color: 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ p: 1.5 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon sx={{ minWidth: 38, color: 'error.main' }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          borderBottomLeftRadius: { sm: 14 },
          borderBottomRightRadius: { sm: 14 },
        }}
      >
        <Toolbar sx={{ minHeight: 72 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
            SYM Matrimonial Admin
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" onClick={handleLogout} sx={{ borderColor: 'rgba(255,255,255,0.45)' }} variant="outlined">
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 10,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
